import { useRef, useCallback, useEffect } from "react";
import { ReactComponent as LogoSvg } from "./logo.svg";
import logoUrl from "./logo.svg";

// ─── Config ──────────────────────────────────────────────────────────────────
const CONFIG = {
  gridSpacing: 4, // px between sampled particle positions (lower = more particles)
  explosionStrength: 550, // max burst distance in px from home position
  burstDuration: 420, // ms for the outward burst phase
  reformDuration: 950, // ms for particles returning home
  particleSizeMin: 0.3, // min particle radius in px
  particleSizeMax: 1.0, // max particle radius in px
  proximityFactor: 0.65, // how much click proximity boosts explosion [0–1]
  maxReformStagger: 180, // max per-particle stagger delay during reform in ms
  alphaThreshold: 60, // min alpha (0–255) for a pixel to become a particle
  scatterX: 20, // max horizontal scatter added to burst position in px
  scatterY: 28, // max vertical scatter added to burst position in px
  particleColor: "#00042d", // logo fill colour
};

// Canvas extends this far beyond the logo on every side so burst positions never clip.
const CANVAS_PAD = Math.ceil(
  CONFIG.explosionStrength + Math.max(CONFIG.scatterX, CONFIG.scatterY) + 40
);

// ─── Easing ──────────────────────────────────────────────────────────────────
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

// ─── Helpers ─────────────────────────────────────────────────────────────────
const rand = (min, max) => min + Math.random() * (max - min);
const clamp01 = (v) => Math.min(1, Math.max(0, v));

// ─── LogoParticles ───────────────────────────────────────────────────────────
function LogoParticles() {
  const containerRef = useRef(null);
  const svgWrapRef = useRef(null); // opacity driven by JS so it syncs with canvas in RAF
  const canvasRef = useRef(null);

  const anim = useRef({
    phase: "idle", // 'idle' | 'burst' | 'reform'
    particles: [],
    phaseStart: 0,
    rafId: null,
    ctx: null,
    cache: null,
  });

  const reducedMotion = useRef(
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => {
    const state = anim.current;
    return () => {
      if (state.rafId) cancelAnimationFrame(state.rafId);
    };
  }, []);

  // ── Sample logo pixels ────────────────────────────────────────────────────
  const buildSample = useCallback(
    (w, h) =>
      new Promise((resolve) => {
        const off = document.createElement("canvas");
        off.width = Math.round(w);
        off.height = Math.round(h);
        const ctx = off.getContext("2d");
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, off.width, off.height);
          const { data } = ctx.getImageData(0, 0, off.width, off.height);
          const sp = CONFIG.gridSpacing;
          const base = [];
          for (let py = 0; py < off.height; py += sp) {
            for (let px = 0; px < off.width; px += sp) {
              const i = (py * off.width + px) * 4;
              if (data[i + 3] >= CONFIG.alphaThreshold) {
                base.push({
                  homeX: px + sp / 2 + CANVAS_PAD,
                  homeY: py + sp / 2 + CANVAS_PAD,
                });
              }
            }
          }
          resolve({
            logoWidth: off.width,
            logoHeight: off.height,
            baseParticles: base,
          });
        };
        img.onerror = () => resolve(null);
        img.src = logoUrl;
      }),
    []
  );

  // ── RAF loop ──────────────────────────────────────────────────────────────
  const loop = useCallback(() => {
    const canvas = canvasRef.current;
    const svgWrap = svgWrapRef.current;
    if (!canvas || !svgWrap) return;

    const { ctx, particles, phase, phaseStart } = anim.current;
    const now = performance.now();

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = CONFIG.particleColor;

    if (phase === "burst") {
      const t = clamp01((now - phaseStart) / CONFIG.burstDuration);
      const ease = easeOutCubic(t);

      ctx.beginPath();
      for (const p of particles) {
        p.x = p.startX + (p.burstX - p.startX) * ease;
        p.y = p.startY + (p.burstY - p.startY) * ease;
        ctx.moveTo(p.x + p.size, p.y);
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      }
      ctx.fill();

      if (t >= 1) {
        anim.current.phase = "reform";
        anim.current.phaseStart = now;
      }
    } else if (phase === "reform") {
      const totalDur = CONFIG.reformDuration + CONFIG.maxReformStagger;
      const elapsed = now - phaseStart;

      // Start crossfade at 65% of reformDuration so particles are clearly still
      // moving when the SVG begins to emerge — the eye follows the motion and
      // doesn't notice the swap. easeInOutSine keeps both ends of the curve
      // smooth so there's no perceptible pop.
      const xfStart = CONFIG.reformDuration * 0.82;
      const xfRaw = clamp01((elapsed - xfStart) / (totalDur - xfStart));
      const xfT = (1 - Math.cos(Math.PI * xfRaw)) / 2; // easeInOutSine
      ctx.globalAlpha = 1 - xfT;

      ctx.beginPath();
      for (const p of particles) {
        const localT = clamp01(
          (elapsed - p.reformDelay) / CONFIG.reformDuration
        );
        const ease = localT <= 0 ? 0 : easeOutQuart(localT);
        p.x = p.burstX + (p.homeX - p.burstX) * ease;
        p.y = p.burstY + (p.homeY - p.burstY) * ease;
        ctx.moveTo(p.x + p.size, p.y);
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      }
      ctx.fill();
      ctx.globalAlpha = 1;

      if (xfT > 0) svgWrap.style.opacity = String(xfT);

      if (elapsed >= totalDur) {
        canvas.style.opacity = "0";
        svgWrap.style.opacity = "1";
        anim.current.phase = "idle";
        anim.current.rafId = requestAnimationFrame(() => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          anim.current.rafId = null;
        });
        return;
      }
    }

    anim.current.rafId = requestAnimationFrame(loop);
  }, []);

  // ── Click handler ─────────────────────────────────────────────────────────
  const handleClick = useCallback(
    async (e) => {
      if (reducedMotion.current) {
        const el = containerRef.current;
        if (!el) return;
        el.classList.remove("logo-particles--flash");
        void el.offsetWidth;
        el.classList.add("logo-particles--flash");
        return;
      }

      const canvas = canvasRef.current;
      const svgWrap = svgWrapRef.current;
      const container = containerRef.current;
      if (!canvas || !svgWrap || !container) return;

      const prevParticles =
        anim.current.phase !== "idle" ? anim.current.particles : null;

      if (anim.current.rafId) {
        cancelAnimationFrame(anim.current.rafId);
        anim.current.rafId = null;
      }

      const rect = container.getBoundingClientRect();
      const cw = rect.width;
      const ch = rect.height;

      const cache = anim.current.cache;
      const needResample =
        !cache ||
        cache.pad !== CANVAS_PAD ||
        Math.abs(cache.logoWidth - cw) > 4 ||
        Math.abs(cache.logoHeight - ch) > 4;

      let sample = cache;
      if (needResample) {
        sample = await buildSample(cw, ch);
        if (!sample) return;
        anim.current.cache = { ...sample, pad: CANVAS_PAD };
      }

      canvas.width = Math.round(cw + CANVAS_PAD * 2);
      canvas.height = Math.round(ch + CANVAS_PAD * 2);
      canvas.style.top = `-${CANVAS_PAD}px`;
      canvas.style.left = `-${CANVAS_PAD}px`;

      const clickX = e.clientX - rect.left + CANVAS_PAD;
      const clickY = e.clientY - rect.top + CANVAS_PAD;
      const maxDist = Math.hypot(cw + CANVAS_PAD * 2, ch + CANVAS_PAD * 2) / 2;

      const particles = sample.baseParticles.map((base, idx) => {
        const dist = Math.hypot(base.homeX - clickX, base.homeY - clickY);
        const proximity = 1 - (dist / maxDist) * CONFIG.proximityFactor;
        const speed = CONFIG.explosionStrength * proximity + rand(0, 30);
        const angle = Math.atan2(base.homeY - clickY, base.homeX - clickX);

        const prev = !needResample && prevParticles ? prevParticles[idx] : null;
        const startX = prev ? prev.x : base.homeX;
        const startY = prev ? prev.y : base.homeY;

        return {
          homeX: base.homeX,
          homeY: base.homeY,
          startX,
          startY,
          x: startX,
          y: startY,
          burstX:
            base.homeX +
            Math.cos(angle) * speed +
            rand(-CONFIG.scatterX, CONFIG.scatterX),
          burstY:
            base.homeY +
            Math.sin(angle) * speed +
            rand(-CONFIG.scatterY, CONFIG.scatterY),
          size: rand(CONFIG.particleSizeMin, CONFIG.particleSizeMax),
          reformDelay: rand(0, CONFIG.maxReformStagger),
        };
      });

      anim.current.particles = particles;
      anim.current.phase = "burst";
      anim.current.phaseStart = performance.now();
      anim.current.ctx = canvas.getContext("2d");

      // Near-zero opacity keeps the SVG painted by the browser (opacity:0
      // lets it skip painting, causing a repaint delay when we restore it).
      svgWrap.style.opacity = "0.001";
      canvas.style.opacity = "1";
      anim.current.rafId = requestAnimationFrame(loop);
    },
    [buildSample, loop]
  );

  return (
    <div className="logo-particles" ref={containerRef} onClick={handleClick}>
      {/* Wrapper div so JS can drive opacity in sync with the canvas RAF */}
      <div ref={svgWrapRef} className="logo-particles__svg-wrap">
        <LogoSvg aria-label="Fergo" role="img" />
      </div>
      <canvas
        ref={canvasRef}
        className="logo-particles__canvas"
        aria-hidden="true"
      />
    </div>
  );
}

export default LogoParticles;
